import { StrictMode } from 'react';
import createCache from '@emotion/cache';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';

import AppProviders from '@/App';
import { routes } from '@/app/routes';
import { createStore, type RootState } from '@/app/store';
import { createI18n } from '@/i18n/i18n';
import { localeFromPath } from '@/i18n/locales';

const locale = localeFromPath(window.location.pathname);
const container = document.getElementById('root');

if (!container) {
  throw new Error('Root container #root is missing from the document');
}

// Present only on prerendered pages; the store then starts with the same data
// the static HTML was built from, so hydration does not flash a skeleton.
const preloadedState = (window as { __PRELOADED_STATE__?: Partial<RootState> })
  .__PRELOADED_STATE__;

const app = (
  <StrictMode>
    <AppProviders
      store={createStore(preloadedState)}
      i18n={createI18n(locale)}
      // Must match the cache key used by the prerenderer, otherwise the
      // server-rendered style tags are not adopted and the page restyles itself.
      emotionCache={createCache({ key: 'goa' })}
    >
      <RouterProvider router={createBrowserRouter(routes)} />
    </AppProviders>
  </StrictMode>
);

document.documentElement.lang = locale;

// Prerendered routes ship real markup; everything else boots as a plain SPA.
if (container.hasChildNodes()) {
  hydrateRoot(container, app);
} else {
  createRoot(container).render(app);
}
